import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { SECTIONS, AGE } from "@/components/admin/reportConfig";
import { ReportItemCard } from "@/components/admin/ReportItemCard";
import { ReportItemForm } from "@/components/admin/ReportItemForm";

const ORDER = [["controllo", "controlli"], ["todo", "todo"], ["open_task", "open_tasks"], ["note", "notes"]];

const Section = ({ type, items, areas, actions, children }) => {
  const [adding, setAdding] = useState(false);
  const cfg = SECTIONS[type];
  const Icon = cfg.icon;
  return (
    <section className={`rounded-3xl border ${cfg.border} ${type === "controllo" ? cfg.bg : "bg-white"} p-4 sm:p-6`} data-testid={cfg.testId}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className={`flex items-center gap-2 text-lg font-bold sm:text-xl ${cfg.text}`}>
          <Icon className="h-5 w-5" /> {cfg.title} <span className="font-mono text-sm font-normal" data-testid={`${cfg.testId}-count`}>{items.length}</span>
        </h3>
        <Button variant="outline" onClick={() => setAdding((a) => !a)} className={`h-10 rounded-full ${cfg.border} ${cfg.text}`} data-testid={`${cfg.testId}-add-btn`}>
          <Plus className="mr-1 h-4 w-4" /> Aggiungi
        </Button>
      </div>
      {adding && (
        <div className="mb-4">
          <ReportItemForm type={type} areas={areas} testId={`${cfg.testId}-add-form`} onCancel={() => setAdding(false)}
            onSubmit={async (v) => { const ok = await actions.create(type, v); if (ok) setAdding(false); return ok; }} />
        </div>
      )}
      {children || (
        <div className="grid gap-3 md:grid-cols-2">
          {!items.length && <p className="text-sm text-[#75867E]">Nessuna voce.</p>}
          {items.map((it) => <ReportItemCard key={it.item_id} item={it} areas={areas} {...actions} />)}
        </div>
      )}
    </section>
  );
};

const FeaturesByArea = ({ features, areas, actions }) => {
  const groups = features.reduce((acc, f) => ({ ...acc, [f.area || "Generale"]: [...(acc[f.area || "Generale"] || []), f] }), {});
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 text-xs text-[#495850]">
        {AGE.map((a) => <span key={a.label} className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${a.dot}`} />{a.label}</span>)}
      </div>
      {Object.keys(groups).sort().map((area) => (
        <div key={area} data-testid={`feature-area-${area}`}>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#75867E]">{area} · {groups[area].length}</h4>
          <div className="grid gap-3 md:grid-cols-2">{groups[area].map((f) => <ReportItemCard key={f.item_id} item={f} areas={areas} {...actions} />)}</div>
        </div>
      ))}
    </div>
  );
};

export const ReportNotes = () => {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const load = useCallback(() => api.get("/admin/report/manifest").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const wrap = (fn, msg) => async (...a) => {
    try { await fn(...a); toast.success(msg); await load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const actions = {
    create: wrap((type, v) => api.post("/admin/report/manifest", { type, ...v }), "Voce aggiunta"),
    onUpdate: wrap((id, v) => api.put(`/admin/report/manifest/${id}`, v), "Voce aggiornata"),
    onDelete: wrap((id) => api.delete(`/admin/report/manifest/${id}`), "Voce eliminata"),
    onPrimary: wrap((it) => api.post(`/admin/report/manifest/${it.item_id}/${it.type === "todo" ? "completa" : "controllato"}`),
      "Fatto!"),
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data: blob } = await api.get("/admin/report/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `iosegnalo-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Generazione PDF non riuscita");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-[#0F2D5E]" />;
  return (
    <div className="space-y-6" data-testid="report-notes">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Report di Avanzamento</h2>
          <p className="text-sm text-[#75867E]">{data.features.length} funzionalità · {data.controlli.length} da controllare</p>
        </div>
        <Button onClick={downloadPdf} disabled={downloading} className="h-12 rounded-full bg-[#0F2D5E] px-6 hover:bg-[#0B2250]" data-testid="btn-download-pdf-report">
          {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Scarica Report PDF
        </Button>
      </div>
      {ORDER.map(([type, key]) => <Section key={type} type={type} items={data[key]} areas={data.areas} actions={actions} />)}
      <Section type="feature" items={data.features} areas={data.areas} actions={actions}>
        <FeaturesByArea features={data.features} areas={data.areas} actions={actions} />
      </Section>
    </div>
  );
};
